import Service from '@ember/service';
import { service } from '@ember/service';
import { getOwner } from '@ember/application';
import { task, timeout } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import tippy, { hideAll } from 'tippy.js';
import normalizeIdentifier from 'ember-stereo/-private/utils/normalize-identifier';
import { registerDestructor } from '@ember/destroyable';
import { SyncChannel } from '../utils/sync-channel';

export default class PlaybackService extends Service {
  @service stereo;
  @service feeds;
  @service store;
  @service session;

  @tracked _closed = false;
  @tracked blockedSound;
  @tracked blockedPrompt;

  get playerIsActive() {
    if (this._closed) return false;
    return !!(this.stereo.currentSound || this.blockedSound);
  }

  _syncChannel = null;

  constructor() {
    super(...arguments);
    this._syncChannel = new SyncChannel({ stereo: this.stereo });
    this.addStereoMonitors();
    registerDestructor(this, this.teardown.bind(this));
  }

  // ---------------------------------------------------------------------------
  // Cross-tab sync — proxied from SyncChannel
  // ---------------------------------------------------------------------------

  get remoteIsPlaying()  { return this._syncChannel.remoteIsPlaying; }
  get remoteUrl()        { return this._syncChannel.remoteUrl; }
  get remoteTabId()      { return this._syncChannel.remoteTabId; }
  get remoteWindowName() { return this._syncChannel.remoteWindowName; }
  get isPlayingRemotely() { return this._syncChannel.isPlayingRemotely; }
  get isPlayingLocally()  { return this._syncChannel.isPlayingLocally; }
  get isPlayingAnywhere() { return this._syncChannel.isPlayingAnywhere; }

  /**
   * Send a command to other tabs.
   * @param {'pause'|'play'|'toggle'} command
   * @param {string} [targetTabId] - Pass this.remoteTabId to target the tab that last played.
   */
  sendCommand(command, targetTabId = null) {
    this._syncChannel.sendCommand(command, targetTabId);
  }

  // ---------------------------------------------------------------------------
  // Setup
  // ---------------------------------------------------------------------------

  addStereoMonitors() {
    this._onAudioPaused = this.onAudioPaused.bind(this);
    this._onAudioPlayed = this.onAudioPlayed.bind(this);
    this._onAudioBlocked = this.onAudioBlocked.bind(this);
    this._onAudioEnded = this.onAudioEnded.bind(this);

    this.stereo.on('audio-played', this._onAudioPlayed);
    this.stereo.on('audio-paused', this._onAudioPaused);
    this.stereo.on('audio-ended', this._onAudioEnded);
    this.stereo.on('audio-blocked', this._onAudioBlocked);
  }

  // ---------------------------------------------------------------------------
  // Stereo event handlers
  // ---------------------------------------------------------------------------

  onAudioPlayed() {
    this._closed = false;
    this._syncChannel.broadcastState({
      isPlaying: true,
      url: this.stereo.currentSound?.identifier,
      windowName: window.name || null,
    });
  }

  onAudioPaused() {
    // Remember where the listener stopped so the scrubber reloads here.
    this.currentPlaybackFeed()?.persistResumePosition();
    this._syncChannel.broadcastState({
      isPlaying: false,
      url: this.stereo.currentSound?.identifier,
      windowName: window.name || null,
    });
  }

  // The single end-of-playback choke point: mark the finished airing fully
  // listened (full progress bar, no resume at the very end), then advance the
  // play queue to the next airing if one is queued.
  onAudioEnded() {
    let feed = this.currentPlaybackFeed();

    // Reaching the end of an airing that's still on the air means catching up
    // to the live edge, not finishing the show: the rest hasn't been broadcast
    // yet. Hand back to the live stream rather than mark it listened (which
    // clears the resume position) and advance past it.
    if (feed?.isInProgress) {
      this.feeds.goLive();
      return;
    }

    feed?.markListened();
    this.advanceQueue();
    this._syncChannel.broadcastState({
      isPlaying: false,
      url: this.stereo.currentSound?.identifier,
      windowName: window.name || null,
    });
  }

  // Optional queue seam: if a host registered a `playback-queue` service, play
  // its next airing when the current one finishes. Inert when none is present.
  advanceQueue() {
    try {
      let queue = getOwner(this)?.lookup('service:playback-queue');
      queue?.advance();
    } catch (e) {
      // no queue service registered — nothing to advance
    }
  }

  currentPlaybackFeed() {
    let sound = this.stereo.currentSound;
    if (!sound) return null;
    let feed = this.feeds.findFeed({ url: sound.identifier });
    return feed?.kind === 'playback' ? feed : null;
  }

  onAudioBlocked({ sound }) {
    this.blockedSound = sound;
    this.handleAudioBlocked.perform(sound);

    this.stereo.one('audio-played', () => {
      this.blockedSound = null;
      if (this.blockedPrompt) {
        hideAll();
        this.blockedPrompt = null;
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Teardown
  // ---------------------------------------------------------------------------

  teardown() {
    if (!this.stereo.isDestroyed) {
      try {
        this.stereo.off('audio-played', this._onAudioPlayed);
        this.stereo.off('audio-blocked', this._onAudioBlocked);
        this.stereo.off('audio-paused', this._onAudioPaused);
        this.stereo.off('audio-ended', this._onAudioEnded);
      } catch (e) {
        // no op
      }
    }
    this._syncChannel.destroy();
  }

  // ---------------------------------------------------------------------------
  // Playback actions
  // ---------------------------------------------------------------------------

  stopAndClose() {
    this.stereo.stop();
    this._closed = true;
  }

  handleAudioBlocked = task({ restartable: true }, async sound => {
    let query = `[data-identifier="${normalizeIdentifier(
      sound.url,
    )}"] .dubbletrack-player-controls`;
    var elements;
    var waitLength = 0;
    var pauseSize = 200;
    while (true) {
      elements = document.querySelectorAll(query);
      waitLength = waitLength + pauseSize;

      await timeout(pauseSize);
      if ((elements && elements.length > 0) || waitLength > 3000) {
        break;
      }
    }

    let instances = tippy(elements, {
      role: 'tooltip',
      theme: 'attention',
      placement: 'bottom',
      maxWidth: '200px',
      animation: 'shift-away-subtle',
      hideOnClick: true,
      interactive: false,
      content:
        'Your browser blocked our autoplay attempt, so give this button a click to listen',
      trigger: 'manual',
      allowHTML: true,
    });

    this.blockedPrompt = instances[0];
    this.blockedPrompt?.show();
    await timeout(1000);
  });
}
